// ScaleBarDraw.js
// Shared drawing helpers to render a consistent scalebar glyph on any 2D canvas.
//
// Copyright Viktor Eberhardt, 2025
// Author: Viktor Eberhardt

// Framework imports
// (none)

// External imports
// (none)

// MUI imports (none)

// HSA imports (none)

/**
 * Draw a consistent scalebar glyph on the given 2D context.
 *
 * Inputs:
 * - ctx: CanvasRenderingContext2D
 * - opts:
 *   - centerX, centerY: baseline center position in canvas pixels
 *   - width: bar width in canvas pixels (already scaled to screen space)
 *   - label: text label to render centered above/below the line
 *   - color: fill color for bar and text (usually white on dark, black on bright)
 *   - pixelScale: size multiplier for line/label thickness; set to 1 for unscaled canvas
 *   - labelAbove: boolean; true to place label above line, false below
 */
export function drawScaleBarGlyph(ctx, opts) {
  if (!ctx || !opts) return;
  const {
    centerX,
    centerY,
    width,
    label = "",
    color = "#fff",
    pixelScale = 1,
    labelAbove = true,
    // optional styling controls to match renderer look without forcing outlines
    fontWeight = "bold",
    outline = false,
    outlineColor = "rgba(0,0,0,1)",
    outlineWidthPx = 2,
  } = opts;
  if (
    !Number.isFinite(centerX) ||
    !Number.isFinite(centerY) ||
    !Number.isFinite(width)
  )
    return;

  // Keep sizes constant in SCREEN pixels by scaling with pixelScale (1 / device scale)
  const lineHeight = 3 * pixelScale;
  const capH = 16 * pixelScale;
  const capW = 2 * pixelScale;
  const fontSize = 14 * pixelScale;
  const fontMargin = 6 * pixelScale;

  // Avoid integer rounding so sub-pixel sizes remain accurate under transform
  const cx = centerX;
  const cy = centerY;
  const left = cx - width / 2;
  const right = left + width;

  // Draw text (optionally with outline). Default mirrors original renderer: bold fill only.
  ctx.save();
  ctx.font = `${fontWeight} ${fontSize}px Arial, sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = labelAbove ? "alphabetic" : "top";
  const textY = labelAbove ? cy - fontMargin : cy + fontMargin;
  if (outline) {
    ctx.lineWidth = Math.max(1, outlineWidthPx * pixelScale);
    ctx.strokeStyle = outlineColor;
    ctx.strokeText(label, cx, textY);
  }
  ctx.fillStyle = color;
  ctx.fillText(label, cx, textY);

  // Draw horizontal line
  ctx.fillStyle = color;
  ctx.fillRect(left, cy - lineHeight / 2, right - left, lineHeight);

  // Draw vertical end stops centered on the line, extending above and below
  const capTop = cy - capH / 2;
  ctx.fillRect(left, capTop, capW, capH);
  ctx.fillRect(right - capW, capTop, capW, capH);
  ctx.restore();
}

/**
 * Convenience to draw a scalebar using the current canvas transform and browser-space bottom-right corner (p2).
 *
 * Expected data shape:
 * - data.width: width of the bar in screen pixels at current zoom (pre-scaled or be scaled by pixelScale outside)
 * - data.maxScaleWidth: bounding width used to place the bar away from the edge (screen pixels at current zoom)
 * - data.label: string
 * - data.x, data.y: additional delta offsets in screen pixels at current zoom
 *
 * Options:
 * - p2: {x, y} bottom-right point of the canvas in screen pixels
 * - isBrightfield: boolean; choose black/white glyph color
 */
export function drawScaleBarFromRenderer(ctx, data, { p2, isBrightfield }) {
  if (!ctx || !data || !p2) return;
  // Derive pixel scale from current transform (handles rotation too)
  const tr = ctx.getTransform();
  const pixelScale = Math.hypot(tr.a, tr.b) || 1; // magnitude of scale
  const marginRight = 5 / pixelScale;
  const marginBottom = 14 / pixelScale;
  const width = Math.max(0, data.width / pixelScale);
  const maxScaleWidth = Math.max(
    width,
    (data.maxScaleWidth || width) / pixelScale
  );
  const deltaX = (data.x || 0) / pixelScale;
  const deltaY = (data.y || 0) / pixelScale;
  const centerX = p2.x - marginRight - maxScaleWidth / 2 + deltaX;
  const centerY = p2.y - marginBottom + deltaY;
  const color = isBrightfield ? "black" : "white";
  drawScaleBarGlyph(ctx, {
    centerX,
    centerY,
    width,
    label: data.label || "",
    color,
    pixelScale: 1 / pixelScale,
    labelAbove: true,
  });
}

export default {
  drawScaleBarGlyph,
  drawScaleBarFromRenderer,
};
